import React, { useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import { hapticTick } from '../../../../../_utils/haptics'
import { useScrollLock } from '../../../../../_utils/useScrollLock'
import { loadPosts, subscribePosts } from '../../social/data/store'
import { POST_TYPES } from '../../social/data/postTypes'
import { postTheme } from '../../social/render/render'
import Preview from '../../social/components/Preview'
import { MailIcon, SmsIcon, PlusIcon, ChevronLeft, ChevronRight, CloseIcon, SparkIcon, InstagramIcon } from '../../social/components/icons'
import { loadCampaigns, loadSitePosts, itemsByDay, monthDays, weekDays, dayKey, FILTERS, KIND_NAMES, itemTime, bandsOn } from '../data/calendarData'
import { useMarketingSites } from '../data/siteScope'
import { siteColour, switchToSite } from '../../../../../_services/sites.service'
import '../Calendar.css'

const PHONE = 767
const usePhone = () => {
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && window.innerWidth <= PHONE)
  useEffect(() => {
    const on = () => setPhone(window.innerWidth <= PHONE)
    window.addEventListener('resize', on)
    return () => window.removeEventListener('resize', on)
  }, [])
  return phone
}

const Seg = ({ value, options, onChange, label, small }) => (
  <div className={`mc-seg${small ? ' is-small' : ''}`} role="radiogroup" aria-label={label}>
    {options.map(o => (
      <button key={o.id} type="button" role="radio" aria-checked={value === o.id} className={`mc-seg-btn${value === o.id ? ' is-on' : ''}`} onClick={() => { if (value !== o.id) { hapticTick(); onChange(o.id) } }}>
        {o.label}
      </button>
    ))}
  </div>
)

const titleOf = (it) => {
  if (it.post) return it.post.name || (POST_TYPES[it.post.type] || {}).label || 'Post'
  return it.title || KIND_NAMES[it.kind]
}
// skipped: a gap-filling text that didn't go because the times it offered
// had filled by then
const STATUS = { planned: 'Planned', posted: 'Shared', scheduled: 'Scheduled', sent: 'Sent', suggested: 'Suggested', skipped: 'Not sent, the times filled' }

// One thing on the calendar: a post's picture, or the channel's icon
const Art = ({ it, kit }) => {
  if (it.post) {
    return (
      <span className="mc-thumb">
        {it.post.thumb ? <img src={it.post.thumb} alt="" /> : kit ? <Preview post={it.post} theme={postTheme(it.post, { kit })} /> : <InstagramIcon size={14} />}
      </span>
    )
  }
  if (it.kind === 'email') return <span className="mc-icon is-email"><MailIcon size={14} /></span>
  if (it.kind === 'sms') return <span className="mc-icon is-sms"><SmsIcon size={14} /></span>
  if (it.kind === 'idea') return <span className="mc-icon is-idea"><SparkIcon size={13} /></span>
  return null
}

const Item = ({ it, kit, onOpen, size = 'chip' }) => {
  if (it.kind === 'moment') {
    return <span className={`mc-moment is-${size}`} title={it.title}>{it.title}</span>
  }
  const time = itemTime(it)
  const site = it.remote ? (it.siteName || 'Another location') : null
  return (
    <button type="button" className={`mc-item is-${size} is-${it.kind} is-${it.status}${it.remote ? ' is-remote' : ''}`} onClick={(e) => { e.stopPropagation(); hapticTick(); onOpen(it) }} title={`${KIND_NAMES[it.kind]}: ${titleOf(it)}${site ? ` · at ${site}` : ''}`}>
      {it.remote && <span className="mc-site-dot" style={{ background: siteColour(it.salonId) }} aria-hidden />}
      <Art it={it} kit={size !== 'chip' ? kit : null} />
      <span className="mc-item-text">
        <span className="mc-item-title">{titleOf(it)}</span>
        <span className="mc-item-meta">{size === 'chip' ? time : [time, KIND_NAMES[it.kind], STATUS[it.status], site].filter(Boolean).join(' · ')}</span>
      </span>
    </button>
  )
}

// Campaigns running on a day: a strip each, named where it starts (and at the
// start of each week row, so a long one is always labelled)
const Bands = ({ bands, day, onOpen, labelAll }) => {
  const list = bandsOn(bands, day)
  if (!list.length) return null
  const monday = moment(day).isoWeekday() === 1
  // Empty rows above a band keep it level with itself on the days either side
  const rows = []
  const top = Math.max(...list.map(b => b.lane || 0))
  for (let i = 0; i <= top; i += 1) rows.push(list.find(b => (b.lane || 0) === i) || { id: `gap${i}`, gap: true })
  return (
    <div className="mc-bands">
      {rows.map(b => (b.gap ? <span key={b.id} className="mc-band-gap" aria-hidden /> : (
        <button key={b.id} type="button" className={`mc-band is-${b.kind}${b.isStart ? ' is-start' : ''}${b.isEnd ? ' is-end' : ''}`}
          onClick={(e) => { e.stopPropagation(); hapticTick(); if (onOpen) onOpen(b) }}
          title={`${b.kind === 'suggested' ? 'Bailey’s suggestion: ' : ''}${b.title}`}>
          {(b.isStart || monday || labelAll) ? <span>{b.kind === 'suggested' ? '✦ ' : ''}{b.title}</span> : <span aria-hidden> </span>}
        </button>
      )))}
    </div>
  )
}

// A day, opened: everything on it and what to add
export const DaySheet = ({ day, items, kit, onOpen, onCreate, createKinds, onClose }) => {
  useScrollLock(true)
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose() } }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onClose])
  const past = moment(day).isBefore(moment(), 'day')
  const kinds = [
    { id: 'post', label: 'A post', sub: 'Instagram or TikTok', icon: <InstagramIcon size={18} /> },
    { id: 'email', label: 'An email', sub: 'To your clients', icon: <MailIcon size={18} /> },
    { id: 'sms', label: 'A text', sub: 'SMS to your clients', icon: <SmsIcon size={18} /> },
    { id: 'bailey', label: 'Ask Bailey', sub: 'A whole campaign, planned', icon: <SparkIcon size={17} /> },
  ].filter(k => createKinds.includes(k.id))
  return (
    <div className="mc-backdrop" onPointerDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="mc-sheet" role="dialog" aria-modal="true" aria-label={moment(day).format('dddd D MMMM')}>
        <div className="mc-sheet-head">
          <h2>{moment(day).calendar(null, { sameDay: '[Today], dddd D MMMM', nextDay: '[Tomorrow], dddd D MMMM', lastDay: '[Yesterday], dddd D MMMM', nextWeek: 'dddd D MMMM', lastWeek: 'dddd D MMMM', sameElse: 'dddd D MMMM' })}</h2>
          <button type="button" className="mc-icon-btn" onClick={onClose} aria-label="Close"><CloseIcon /></button>
        </div>
        {items.length ? (
          <div className="mc-day-list">
            {items.map(it => <Item key={it.key} it={it} kit={kit} onOpen={(x) => { onClose(); onOpen(x) }} size="row" />)}
          </div>
        ) : <p className="mc-empty">Nothing on this day yet.</p>}
        {!past && kinds.length > 0 && (
          <>
            <h3 className="mc-sheet-sub">Add to this day</h3>
            <div className="mc-create">
              {kinds.map(k => (
                <button key={k.id} type="button" className={`mc-create-btn${k.id === 'bailey' ? ' is-bailey' : ''}`} onClick={() => { hapticTick(); onClose(); onCreate(day, k.id) }}>
                  <span className="mc-create-icon">{k.icon}</span>
                  <span><strong>{k.label}</strong><small>{k.sub}</small></span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// The marketing calendar. mode "full" is the Calendar page (month or week);
// "embed" is the week strip on other pages. Phones always get the week as a
// Another location's item (Locations filter): it can be seen here but only
// opened or changed at its own location, like the diary's other-site blocks.
const RemoteSheet = ({ it, onClose }) => {
  useScrollLock(true)
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose() } }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onClose])
  const site = it.siteName || 'another location'
  const go = async () => {
    setBusy(true)
    try { await switchToSite(it.salonId, window.location.pathname) } catch (e) { setBusy(false) }
  }
  return (
    <div className="mc-backdrop" onPointerDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="mc-sheet mc-remote" role="dialog" aria-modal="true" aria-label={`At ${site}`}>
        <div className="mc-sheet-head">
          <h2><span className="mc-site-dot" style={{ background: siteColour(it.salonId) }} aria-hidden />At {site}</h2>
          <button type="button" className="mc-icon-btn" onClick={onClose} aria-label="Close"><CloseIcon /></button>
        </div>
        <p className="mc-remote-what"><b>{titleOf(it)}</b><br />{[moment(it.at).format('ddd D MMM'), itemTime(it), KIND_NAMES[it.kind], STATUS[it.status]].filter(Boolean).join(' · ')}</p>
        <p className="mc-remote-note">This belongs to {site}. Switch to that location to open or change it.</p>
        <div className="mc-remote-actions">
          <button type="button" className="mc-remote-btn" onClick={onClose}>Close</button>
          <button type="button" className="mc-remote-btn is-primary" onClick={go} disabled={busy}>{busy ? 'Switching…' : `Switch to ${site}`}</button>
        </div>
      </div>
    </div>
  )
}

// list of days, every day shown.
const MarketingCalendar = ({
  dispatch, trade = 'hair', kit, posts: givenPosts, plans = [], mode = 'embed', initialFilter = 'all',
  title = 'Calendar', createKinds = ['post', 'email', 'sms', 'bailey'],
  onOpenPost, onOpenCampaign, onOpenPlan, onCreate, onOpenFull, headExtra, showLocations = true, bands = [], onOpenBand, onReady,
}) => {
  const phone = usePhone()
  const [view, setView] = useState(mode === 'full' ? 'month' : 'week')
  const [anchor, setAnchor] = useState(() => new Date())
  const [filter, setFilter] = useState(initialFilter)
  const [campaigns, setCampaigns] = useState([])
  const [ownPosts, setOwnPosts] = useState(() => (givenPosts ? [] : loadPosts()))
  const [openDay, setOpenDay] = useState(null)
  const [remote, setRemote] = useState(null)
  const [sitePosts, setSitePosts] = useState([])
  const locations = useMarketingSites()

  useEffect(() => {
    if (givenPosts) return undefined
    return subscribePosts(() => setOwnPosts(loadPosts()))
  }, [givenPosts])
  const hereP = givenPosts || ownPosts
  const posts = useMemo(() => (sitePosts.length ? [...hereP, ...sitePosts] : hereP), [hereP, sitePosts])
  const shown = phone ? 'agenda' : view
  const days = shown === 'month' ? monthDays(anchor) : weekDays(anchor)
  // The campaigns for exactly the days on show (one call per channel)
  const rangeFrom = days[0].format('YYYY-MM-DD')
  const rangeTo = days[days.length - 1].format('YYYY-MM-DD')
  // Told once the first range's campaigns are in, so a page can hold its
  // space until then instead of growing around the calendar
  const readyRef = useRef(onReady)
  readyRef.current = onReady
  const told = useRef(false)
  const tell = () => { if (!told.current && readyRef.current) { told.current = true; readyRef.current() } }
  useEffect(() => {
    let alive = true
    loadCampaigns(dispatch, { from: rangeFrom, to: rangeTo, salonIds: locations.param }).then((l) => { if (alive) setCampaigns(l) }).catch(() => {}).then(() => { if (alive) tell() })
    if (locations.param) loadSitePosts({ from: rangeFrom, to: rangeTo, salonIds: locations.param }).then((l) => { if (alive) setSitePosts(l) }).catch(() => { if (alive) setSitePosts([]) })
    else setSitePosts([])
    return () => { alive = false }
  }, [dispatch, rangeFrom, rangeTo, locations.param])
  const byDay = useMemo(() => itemsByDay({ from: days[0].toDate(), to: days[days.length - 1].toDate(), posts, campaigns, plans, trade, filter }),
    [days[0].valueOf(), posts, campaigns, plans, trade, filter]) // eslint-disable-line react-hooks/exhaustive-deps
  const unit = shown === 'month' ? 'month' : 'week'
  const range = shown === 'month'
    ? moment(anchor).format('MMMM YYYY')
    : `${days[0].format('D MMM')} to ${days[6].format('D MMM')}`
  const isNow = shown === 'month' ? moment(anchor).isSame(moment(), 'month') : moment(anchor).isSame(moment(), 'isoWeek')
  const today = dayKey(new Date())

  const open = (it) => {
    if (it.remote) setRemote(it)
    else if (it.post && onOpenPost) onOpenPost(it.post)
    else if ((it.kind === 'email' || it.kind === 'sms') && onOpenCampaign) onOpenCampaign(it.campaign || it)
    else if (it.kind === 'idea' && onOpenPlan) onOpenPlan(it)
  }
  const create = (d, kind) => { if (onCreate) onCreate(d.toDate ? d.toDate() : d, kind) }
  const past = (d) => d.isBefore(moment(), 'day')

  const head = (
    <div className="mc-head">
      <div className="mc-head-title">
        {title && <h2 className="mc-title">{title}</h2>}
        <div className="mc-nav">
          <button type="button" className="mc-icon-btn" onClick={() => { hapticTick(); setAnchor(a => moment(a).subtract(1, unit).toDate()) }} aria-label={`Previous ${unit}`}><ChevronLeft /></button>
          <span className="mc-range" aria-live="polite">{range}</span>
          <button type="button" className="mc-icon-btn" onClick={() => { hapticTick(); setAnchor(a => moment(a).add(1, unit).toDate()) }} aria-label={`Next ${unit}`}><ChevronRight /></button>
          {!isNow && <button type="button" className="mc-today" onClick={() => { hapticTick(); setAnchor(new Date()) }}>Today</button>}
        </div>
      </div>
      <div className="mc-head-tools">
        <Seg small label="Show" value={filter} onChange={setFilter} options={FILTERS} />
        {!phone && mode === 'full' && <Seg small label="View" value={view} onChange={setView} options={[{ id: 'month', label: 'Month' }, { id: 'week', label: 'Week' }]} />}
        {onOpenFull && <button type="button" className="mc-link" onClick={onOpenFull}>Open calendar</button>}
        {headExtra}
      </div>
    </div>
  )

  let body = null
  if (shown === 'month') {
    body = (
      <div className="mc-month">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => <span key={d} className="mc-dow">{d}</span>)}
        {days.map((d) => {
          const k = dayKey(d)
          const items = byDay[k] || []
          const other = !d.isSame(moment(anchor), 'month')
          return (
            <div key={k} className={`mc-cell${k === today ? ' is-today' : ''}${other ? ' is-other' : ''}${past(d) ? ' is-past' : ''}`} onClick={() => setOpenDay(d)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') setOpenDay(d) }} aria-label={`${d.format('dddd D MMMM')}, ${items.length ? `${items.length} things` : 'nothing yet'}`}>
              <span className="mc-cell-num">{d.format('D')}</span>
              {!past(d) && onCreate && <button type="button" className="mc-add" onClick={(e) => { e.stopPropagation(); setOpenDay(d) }} aria-label={`Add to ${d.format('dddd D MMMM')}`}><PlusIcon size={13} /></button>}
              <Bands bands={bands} day={d} onOpen={onOpenBand} />
              <div className="mc-cell-items">
                {items.slice(0, 3).map(it => <Item key={it.key} it={it} onOpen={open} />)}
                {items.length > 3 && <span className="mc-more">{items.length - 3} more</span>}
              </div>
            </div>
          )
        })}
      </div>
    )
  } else if (shown === 'week') {
    body = (
      <div className="mc-week">
        {days.map((d) => {
          const k = dayKey(d)
          const items = byDay[k] || []
          return (
            <div key={k} className={`mc-col${k === today ? ' is-today' : ''}${past(d) ? ' is-past' : ''}`}>
              <button type="button" className="mc-col-head" onClick={() => setOpenDay(d)} aria-label={`Open ${d.format('dddd D MMMM')}`}>
                <span className="mc-col-dow">{d.format('ddd')}</span>
                <span className="mc-col-num">{d.format('D')}</span>
              </button>
              <Bands bands={bands} day={d} onOpen={onOpenBand} />
              <div className="mc-col-items">
                {items.map(it => <Item key={it.key} it={it} kit={kit} onOpen={open} size="card" />)}
                {!past(d) && onCreate && (
                  <button type="button" className="mc-col-add" onClick={() => setOpenDay(d)} aria-label={`Add to ${d.format('dddd D MMMM')}`}><PlusIcon size={14} /></button>
                )}
              </div>
            </div>
          )
        })}
      </div>
    )
  } else {
    body = (
      <div className="mc-agenda">
        {days.map((d) => {
          const k = dayKey(d)
          const items = byDay[k] || []
          return (
            <div key={k} className={`mc-row${k === today ? ' is-today' : ''}${past(d) ? ' is-past' : ''}${items.length ? '' : ' is-empty'}`}>
              <button type="button" className="mc-row-day" onClick={() => setOpenDay(d)} aria-label={`Open ${d.format('dddd D MMMM')}`}>
                <span className="mc-col-dow">{d.format('ddd')}</span>
                <span className="mc-col-num">{d.format('D')}</span>
              </button>
              <div className="mc-row-items">
                <Bands bands={bands} day={d} onOpen={onOpenBand} labelAll />
                {items.length
                  ? items.map(it => <Item key={it.key} it={it} kit={kit} onOpen={open} size="row" />)
                  : <span className="mc-row-empty">{past(d) ? 'Nothing sent' : 'Nothing planned'}</span>}
              </div>
              {!past(d) && onCreate && <button type="button" className="mc-row-add" onClick={() => setOpenDay(d)} aria-label={`Add to ${d.format('dddd D MMMM')}`}><PlusIcon size={16} /></button>}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <section className={`mc-root is-${mode} is-${shown}`} aria-label={title || 'Calendar'}>
      {head}
      {body}
      <div className="mc-key" aria-hidden>
        <span><i className="is-post" />Posts</span>
        <span><i className="is-email" />Emails</span>
        <span><i className="is-sms" />Texts</span>
        <span><i className="is-moment" />Dates to know</span>
        {plans.length > 0 && <span><i className="is-idea" />Bailey’s ideas</span>}
        {bands.length > 0 && <span><i className="is-band" />Campaigns</span>}
      </div>
      {openDay && (
        <DaySheet
          day={openDay.toDate ? openDay.toDate() : openDay}
          items={byDay[dayKey(openDay)] || []}
          kit={kit}
          onOpen={open}
          onCreate={create}
          createKinds={onCreate ? createKinds : []}
          onClose={() => setOpenDay(null)}
        />
      )}
      {remote && <RemoteSheet it={remote} onClose={() => setRemote(null)} />}
    </section>
  )
}

export default MarketingCalendar
